import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { Panel, Badge } from '../design/primitives';
import { Spinner } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import {
  FlagIcon,
  FireIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SparklesIcon,
  // Intensity-based icons
  BoltIcon,        // intense workout
  HeartIcon,       // recovery/sleep
} from '@heroicons/react/24/outline';

// Walking icon SVG (not in heroicons)
const WalkingIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="4" r="2" />
    <path d="M7 21l3-9-2.5-1L11 6l4 5-3 1 2 9" />
  </svg>
);

// Running/sprinting icon SVG
const RunningIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="14" cy="4" r="2" />
    <path d="M4 17l4-1 3-7 4 2 5-4" />
    <path d="M11 10l-2 7-5 1" />
    <path d="M15 8l2 8 3 1" />
  </svg>
);

interface HealthGoal {
  id: number;
  name: string;
  description: string;
  goal_type: 'outcome' | 'process';
  goal_type_display: string;
  metric_type: string;
  metric_type_display: string;
  target_value: number;
  target_unit: string;
  frequency: string;
  frequency_display: string;
  comparison: string;
  comparison_display: string;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  progress_percentage: number;
  current_streak: number;
  current_value?: number;
  intensity_category?: 'light' | 'moderate' | 'intense' | 'recovery' | 'general';
  source?: 'manual' | 'coaching';
  source_display?: string;
}

interface ProgressionRecommendation {
  metric: string;
  action: string;  // 'increase' | 'decrease' - using string for API compatibility
  current: number;
  proposed: number;
  reason: string;
}

// Intensity category styling
const INTENSITY_STYLES: Record<string, { color: string; bgColor: string; icon: React.FC<{ style?: React.CSSProperties }> }> = {
  light: {
    color: '#10B981', // green
    bgColor: 'rgba(16, 185, 129, 0.15)',
    icon: WalkingIcon,
  },
  moderate: {
    color: COLORS.accent.default,
    bgColor: 'rgba(59, 130, 246, 0.15)',
    icon: WalkingIcon,
  },
  intense: {
    color: '#EF4444', // red
    bgColor: 'rgba(239, 68, 68, 0.15)',
    icon: BoltIcon,
  },
  recovery: {
    color: '#8B5CF6', // purple
    bgColor: 'rgba(139, 92, 246, 0.15)',
    icon: HeartIcon,
  },
  general: {
    color: COLORS.status.warning,
    bgColor: 'rgba(245, 158, 11, 0.15)',
    icon: FireIcon,
  },
};

interface HealthGoalsSummaryProps {
  preloadedGoals?: HealthGoal[];
  progressionRecommendations?: ProgressionRecommendation[];
  onApplyProgression?: (metric: string) => void;
}

const HealthGoalsSummary: React.FC<HealthGoalsSummaryProps> = ({
  preloadedGoals,
  progressionRecommendations,
  onApplyProgression,
}) => {
  const [goals, setGoals] = useState<HealthGoal[]>(preloadedGoals || []);
  const [loading, setLoading] = useState(!preloadedGoals);
  const [error, setError] = useState<string | null>(null);

  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/health-goals/', {
        params: { status: 'active' },
      });
      setGoals(response.data.results || response.data);
    } catch (err: any) {
      console.error('Error fetching health goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if no preloaded data
    if (!preloadedGoals) {
      fetchGoals();
    }
  }, [fetchGoals, preloadedGoals]);

  // Group goals by type
  const outcomeGoals = goals.filter((g) => g.goal_type === 'outcome');
  const processGoals = goals.filter((g) => g.goal_type === 'process');

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return COLORS.status.success;
    if (percentage >= 75) return COLORS.accent.default;
    if (percentage >= 50) return COLORS.status.warning;
    return COLORS.status.error;
  };

  if (loading) {
    return (
      <Panel>
        <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[24] }}>
          <Spinner size="md" />
        </div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <div style={{
          padding: SPACING[16],
          color: COLORS.status.error,
          fontSize: TYPOGRAPHY.size.sm,
          textAlign: 'center',
        }}>
          {error}
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: SPACING[16],
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <FlagIcon style={{ width: 18, height: 18, color: COLORS.accent.default }} />
          <h3 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            Health Goals
          </h3>
        </div>
        <Link
          to="/health/goals"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING[4],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.accent.default,
            textDecoration: 'none',
          }}
        >
          Manage
          <ChevronRightIcon style={{ width: 14, height: 14 }} />
        </Link>
      </div>

      {goals.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: SPACING[24],
          color: COLORS.text.secondary,
          fontSize: TYPOGRAPHY.size.sm,
        }}>
          <FlagIcon style={{ width: 32, height: 32, color: COLORS.text.tertiary, margin: '0 auto', marginBottom: SPACING[8] }} />
          <p style={{ margin: 0 }}>No active goals set</p>
          <Link
            to="/health/goals"
            style={{
              color: COLORS.accent.default,
              fontSize: TYPOGRAPHY.size.sm,
              textDecoration: 'none',
            }}
          >
            Create your first goal
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          {/* Outcome Goals Section */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACING[4],
              marginBottom: SPACING[8],
            }}>
              <Badge color="accent">Outcome</Badge>
              <span style={{
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.tertiary,
              }}>
                Target specific results
              </span>
            </div>
            {outcomeGoals.length === 0 ? (
              <div style={{
                padding: SPACING[12],
                backgroundColor: COLORS.bg.base,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.size.sm,
                color: COLORS.text.tertiary,
                textAlign: 'center',
              }}>
                No outcome goals set
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                {outcomeGoals.map((goal) => (
                  <GoalCard key={goal.id} goal={goal} getProgressColor={getProgressColor} />
                ))}
              </div>
            )}
          </div>

          {/* Process Goals Section */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACING[4],
              marginBottom: SPACING[8],
            }}>
              <Badge color="success">Process</Badge>
              <span style={{
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.tertiary,
              }}>
                Track consistent behaviors
              </span>
            </div>
            {processGoals.length === 0 ? (
              <div style={{
                padding: SPACING[12],
                backgroundColor: COLORS.bg.base,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.size.sm,
                color: COLORS.text.tertiary,
                textAlign: 'center',
              }}>
                No process goals set
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                {processGoals.map((goal) => {
                  // Find progression recommendation for this goal's metric
                  const metricMap: Record<string, string> = {
                    'steps': 'steps',
                    'strength_sessions': 'strength_sessions',
                    'cardio_sessions': 'cardio_sessions',
                  };
                  const recommendation = progressionRecommendations?.find(
                    r => r.metric === metricMap[goal.metric_type]
                  );
                  return (
                    <GoalCard
                      key={goal.id}
                      goal={goal}
                      getProgressColor={getProgressColor}
                      isProcess
                      progressionRecommendation={recommendation}
                      onApplyProgression={onApplyProgression}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

// Individual goal card component
const GoalCard: React.FC<{
  goal: HealthGoal;
  getProgressColor: (percentage: number) => string;
  isProcess?: boolean;
  progressionRecommendation?: ProgressionRecommendation;
  onApplyProgression?: (metric: string) => void;
}> = ({ goal, getProgressColor, isProcess = false, progressionRecommendation, onApplyProgression }) => {
  const intensity = goal.intensity_category || 'general';
  const intensityStyle = INTENSITY_STYLES[intensity];
  const IconComponent = intensityStyle.icon;

  // Parse values to numbers (API may return strings)
  const currentValue = typeof goal.current_value === 'number'
    ? goal.current_value
    : parseFloat(String(goal.current_value)) || 0;
  const targetValue = typeof goal.target_value === 'number'
    ? goal.target_value
    : parseFloat(String(goal.target_value)) || 1;

  // Calculate current progress percentage toward today's/this week's target
  const progressPct = Math.min(Math.round((currentValue / targetValue) * 100), 100);
  const isComplete = currentValue >= targetValue;

  // Format value for display (remove decimals if whole number)
  const formatValue = (val: number | string | null | undefined) => {
    const num = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  return (
    <div style={{
      padding: SPACING[12],
      backgroundColor: COLORS.bg.base,
      borderRadius: RADIUS,
      display: 'flex',
      alignItems: 'center',
      gap: SPACING[12],
      borderLeft: `3px solid ${intensityStyle.color}`,
    }}>
      {/* Intensity icon */}
      <div style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: intensityStyle.bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <IconComponent style={{
          width: 14,
          height: 14,
          color: intensityStyle.color,
        }} />
      </div>

      {/* Goal info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[8],
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {goal.name}
          </span>
          {/* Adaptive badge for coaching goals */}
          {goal.source === 'coaching' && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: SPACING[4],
              padding: `2px 6px`,
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.size.xs,
              fontWeight: TYPOGRAPHY.weight.medium,
              color: '#8B5CF6',
            }}>
              <SparklesIcon style={{ width: 10, height: 10 }} />
              Adaptive
            </span>
          )}
        </div>
        <div style={{
          fontSize: TYPOGRAPHY.size.xs,
          color: COLORS.text.secondary,
          marginTop: SPACING[4],
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[8],
        }}>
          {/* Current progress for process goals */}
          {isProcess && (
            <span style={{
              color: isComplete ? COLORS.status.success : COLORS.text.secondary,
              fontWeight: isComplete ? TYPOGRAPHY.weight.semibold : TYPOGRAPHY.weight.normal,
            }}>
              {formatValue(currentValue)}/{formatValue(targetValue)} {goal.target_unit}
              {goal.frequency === 'weekly' && ' this week'}
            </span>
          )}
          {!isProcess && (
            <span>
              {goal.comparison_display || ''} {formatValue(targetValue)} {goal.target_unit} {(goal.frequency_display || '').toLowerCase()}
            </span>
          )}
          {/* Progression recommendation */}
          {progressionRecommendation && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: SPACING[4],
                padding: `2px 6px`,
                backgroundColor: progressionRecommendation.action === 'increase'
                  ? 'rgba(16, 185, 129, 0.15)'
                  : 'rgba(245, 158, 11, 0.15)',
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.size.xs,
                color: progressionRecommendation.action === 'increase'
                  ? COLORS.status.success
                  : COLORS.status.warning,
                cursor: onApplyProgression ? 'pointer' : 'default',
              }}
              onClick={onApplyProgression ? () => onApplyProgression(progressionRecommendation.metric) : undefined}
              title={progressionRecommendation.reason}
            >
              {progressionRecommendation.action === 'increase' ? (
                <ArrowTrendingUpIcon style={{ width: 10, height: 10 }} />
              ) : (
                <ArrowTrendingDownIcon style={{ width: 10, height: 10 }} />
              )}
              {progressionRecommendation.action === 'increase' ? '+' : ''}
              {progressionRecommendation.proposed - progressionRecommendation.current}
            </span>
          )}
        </div>
      </div>

      {/* Process goals: Show progress bar + streak */}
      {isProcess && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[8],
          flexShrink: 0,
        }}>
          {/* Mini progress bar */}
          <div style={{
            width: 40,
            height: 4,
            backgroundColor: COLORS.border.default,
            borderRadius: RADIUS,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${progressPct}%`,
              height: '100%',
              backgroundColor: isComplete ? COLORS.status.success : intensityStyle.color,
              transition: 'width 0.3s ease',
            }} />
          </div>

          {/* Streak badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING[4],
            padding: `${SPACING[4]} ${SPACING[8]}`,
            backgroundColor: goal.current_streak > 0 ? 'rgba(245, 158, 11, 0.15)' : COLORS.bg.hover,
            borderRadius: RADIUS,
          }}>
            <FireIcon style={{
              width: 12,
              height: 12,
              color: goal.current_streak > 0 ? COLORS.status.warning : COLORS.text.tertiary
            }} />
            <span style={{
              fontSize: TYPOGRAPHY.size.xs,
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: goal.current_streak > 0 ? COLORS.status.warning : COLORS.text.tertiary,
            }}>
              {goal.current_streak}d
            </span>
          </div>
        </div>
      )}

      {/* Outcome goals: Show progress toward target */}
      {!isProcess && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[8],
          flexShrink: 0,
        }}>
          <div style={{
            width: 50,
            height: 6,
            backgroundColor: COLORS.border.default,
            borderRadius: RADIUS,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min(goal.progress_percentage, 100)}%`,
              height: '100%',
              backgroundColor: getProgressColor(goal.progress_percentage),
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: COLORS.text.secondary,
            minWidth: 32,
          }}>
            {goal.progress_percentage}%
          </span>
        </div>
      )}
    </div>
  );
};

export default HealthGoalsSummary;
